// Leverandørsortiment: hvilke massetyper og undervalg en masseleverandør (hurtigvalg-hentested) leverer.
// offers: { [typeKey]: [optionKey, ...] }. Uten offers (eller tomt) er sortimentet ikke registrert,
// og da gjelder ingen begrensning.
import { activeMassTypes, activeOptions, findMassType } from './massCatalog';

const norm = (s) => String(s ?? '').trim().toLowerCase();
const COORD_EPS = 1e-4;

// Leverandøren som ligger på valgt hentested (samme gate og koordinater), ellers null.
export function supplierAt(pickup, locations) {
  if (!pickup?.street || pickup.lat == null || pickup.lng == null) return null;
  return (locations ?? []).find((l) =>
    norm(l.street) === norm(pickup.street)
    && Math.abs(l.lat - pickup.lat) < COORD_EPS
    && Math.abs(l.lng - pickup.lng) < COORD_EPS) ?? null;
}

// Leverandøren et oppdrag ble hentet hos: lagret id først, ellers utledet fra hentestedet (eldre oppdrag).
export function supplierForOrder(order, locations) {
  if (!order) return null;
  const byId = order.pickupSupplierId && (locations ?? []).find((l) => l.id === order.pickupSupplierId);
  return byId || supplierAt(order.pickupAddress, locations);
}

export const hasOffers = (supplier) => !!supplier?.offers && Object.keys(supplier.offers).length > 0;

// Aktive massetyper leverandøren tilbyr. null = ingen begrensning.
export function offeredTypeKeys(supplier, catalog) {
  if (!hasOffers(supplier)) return null;
  return activeMassTypes(catalog)
    .filter((t) => {
      const offered = supplier.offers[t.key];
      if (!Array.isArray(offered)) return false;
      const opts = activeOptions(t);
      return opts.length === 0 || opts.some((o) => offered.includes(o.key));
    })
    .map((t) => t.key);
}

// Aktive undervalg leverandøren tilbyr for typen. null = ingen begrensning (eller typen har ingen undervalg).
export function offeredOptionKeys(supplier, typeKey, catalog) {
  if (!hasOffers(supplier)) return null;
  const opts = activeOptions(findMassType(typeKey, catalog));
  if (opts.length === 0) return null;
  const offered = supplier.offers[typeKey] ?? [];
  return opts.filter((o) => offered.includes(o.key)).map((o) => o.key);
}

export function isOffered(supplier, typeKey, optionKey, catalog) {
  const types = offeredTypeKeys(supplier, catalog);
  if (types === null) return true;
  if (!types.includes(typeKey)) return false;
  if (!optionKey) return true;
  const opts = offeredOptionKeys(supplier, typeKey, catalog);
  return opts === null || opts.includes(optionKey);
}

// Stedene (utsalg eller mottak) som fører varen. Steder uten registrert sortiment er ikke begrenset.
export function offeredPlaces(places, typeKey, optionKey, catalog) {
  return (Array.isArray(places) ? places : []).filter((p) => isOffered(p, typeKey, optionKey || null, catalog));
}

// Visningstekst for hurtigvalg-brikken: navn på tilbudte aktive typer, maks tre.
export function offersSubtitle(offers, catalog) {
  const labels = activeMassTypes(catalog).filter((t) => Array.isArray(offers?.[t.key])).map((t) => t.label);
  if (labels.length === 0) return '';
  return labels.length > 3 ? `${labels.slice(0, 3).join(', ')}…` : labels.join(', ');
}

// Feilmelding for ugyldig sortiment (admin), ellers null.
export function validateOffers(offers, catalog) {
  for (const [typeKey, optionKeys] of Object.entries(offers ?? {})) {
    const type = findMassType(typeKey, catalog);
    if (!type) return `Ukjent massetype: ${typeKey}`;
    if (!Array.isArray(optionKeys)) return `Ugyldig sortiment for ${type.label}`;
    const known = new Set(type.options.map((o) => o.key));
    if (optionKeys.some((k) => !known.has(k))) return `Ukjent undervalg for ${type.label}`;
    if (!type.active) continue;
    const opts = activeOptions(type);
    if (opts.length > 0 && !opts.some((o) => optionKeys.includes(o.key))) {
      return `Velg minst ett undervalg for ${type.label}`;
    }
  }
  return null;
}
